
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BikeRevenueData } from './types';
import { TrendingUp, Target, Award, AlertTriangle } from 'lucide-react';

interface PaybackMetricsCardProps {
  data: BikeRevenueData[];
  targetRevenue?: number;
}

const PaybackMetricsCard: React.FC<PaybackMetricsCardProps> = ({ 
  data, 
  targetRevenue = 55000 
}) => {
  const metrics = React.useMemo(() => {
    const totalBikes = data.length;
    const fullyPaidBack = data.filter(bike => bike.totalRevenue >= targetRevenue).length;
    const averagePaybackPercentage = data.length > 0 
      ? data.reduce((sum, bike) => sum + (bike.totalRevenue / targetRevenue) * 100, 0) / data.length
      : 0;
    
    const topPerformers = data
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 3);
    
    const underPerformers = data
      .filter(bike => bike.totalRevenue < targetRevenue * 0.2)
      .sort((a, b) => a.totalRevenue - b.totalRevenue)
      .slice(0, 3);

    return {
      totalBikes,
      fullyPaidBack,
      paybackRate: totalBikes > 0 ? (fullyPaidBack / totalBikes) * 100 : 0,
      averagePaybackPercentage,
      topPerformers,
      underPerformers
    };
  }, [data, targetRevenue]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Метрики окупаемости велосипедов
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-600">
              {metrics.fullyPaidBack}
            </div>
            <div className="text-sm text-muted-foreground">
              Полностью окупились
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {metrics.paybackRate.toFixed(1)}%
            </div>
            <div className="text-sm text-muted-foreground">
              Доля окупившихся
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {metrics.averagePaybackPercentage.toFixed(1)}%
            </div>
            <div className="text-sm text-muted-foreground">
              Средняя окупаемость
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold">
              {targetRevenue.toLocaleString()} ₽
            </div>
            <div className="text-sm text-muted-foreground">
              Целевая выручка
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {metrics.topPerformers.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Award className="h-4 w-4 text-emerald-600" />
                <span className="font-medium text-emerald-600">ТОП-3 лучших</span>
              </div>
              <div className="space-y-2">
                {metrics.topPerformers.map((bike, index) => (
                  <div key={bike.bikeId} className="flex justify-between items-center text-sm">
                    <span className="font-medium">
                      #{index + 1} {bike.serialNumber}
                    </span>
                    <span className="text-emerald-600 font-medium">
                      {bike.totalRevenue.toLocaleString()} ₽
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {metrics.underPerformers.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="font-medium text-red-600">Требуют внимания</span>
              </div>
              <div className="space-y-2">
                {metrics.underPerformers.map((bike) => (
                  <div key={bike.bikeId} className="flex justify-between items-center text-sm">
                    <span className="font-medium">
                      {bike.serialNumber}
                    </span>
                    <span className="text-red-600 font-medium">
                      {bike.totalRevenue.toLocaleString()} ₽
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PaybackMetricsCard;
